import { useCallback, useMemo } from "react";

import { readPreference, usePreference, writePreference } from "@/features/preferences/store";

/**
 * Per staff member: whether "Get your shop ready" is hidden and whether the home page tour was
 * seen (T11-47). Kept in the person's account at identity-service (T01-17, preference key
 * `onboarding`) so it follows them to every computer; *what is done* is never stored here — it
 * always comes from the services (`useOnboardingStatus`, design/10 §7.10 T01-D5).
 */
export interface OnboardingPrefs {
  checklistHidden: boolean;
  coachDone: boolean;
}

export const ONBOARDING_KEY = "onboarding";
const DEFAULTS: OnboardingPrefs = { checklistHidden: false, coachDone: false };

function parse(value: unknown): OnboardingPrefs {
  if (typeof value !== "object" || value === null) {
    return DEFAULTS;
  }
  const parsed = value as Partial<OnboardingPrefs>;
  return {
    checklistHidden: parsed.checklistHidden === true,
    coachDone: parsed.coachDone === true,
  };
}

export function readPrefs(userId: string): OnboardingPrefs {
  return parse(readPreference(userId, ONBOARDING_KEY));
}

export function writePrefs(userId: string, patch: Partial<OnboardingPrefs>): void {
  writePreference(userId, ONBOARDING_KEY, { ...readPrefs(userId), ...patch });
}

export function useOnboardingPrefs(userId: string | undefined) {
  const id = userId ?? "anonymous";
  const stored = usePreference(id, ONBOARDING_KEY);
  const prefs = useMemo(() => parse(stored), [stored]);
  const update = useCallback(
    (patch: Partial<OnboardingPrefs>) => {
      writePrefs(id, patch);
    },
    [id],
  );
  return [prefs, update] as const;
}
